import type { JsonSchema } from "./catalog";

/**
 * Build the smallest payload that satisfies an OCPP JSON schema: only required properties,
 * minimum array lengths, enum values picked by preference. Used to answer every message a
 * peer may send with a schema-valid payload, with per-action overrides layered on top.
 */
export interface MinimalPayloadOptions {
  /** Enum values tried in order before falling back to the first value of the enum. */
  preferredEnumValues?: readonly string[];
  now?: () => Date;
}

const DEFAULT_PREFERRED_ENUM_VALUES = ["Accepted"];

const resolveRef = (schema: JsonSchema, root: JsonSchema): JsonSchema => {
  let current = schema;
  const seen = new Set<string>();
  while (current?.$ref) {
    const ref: string = current.$ref;
    if (seen.has(ref)) throw new Error(`Circular $ref ${ref}`);
    seen.add(ref);
    if (!ref.startsWith("#/")) throw new Error(`Unsupported $ref ${ref}`);
    current = ref
      .slice(2)
      .split("/")
      .reduce((node: any, key) => node?.[key], root);
    if (!current) throw new Error(`Unresolved $ref ${ref}`);
  }
  return current;
};

const pickEnum = (values: readonly unknown[], preferred: readonly string[]) =>
  preferred.find(value => values.includes(value)) ?? values[0];

export const buildMinimalPayload = (
  schema: JsonSchema,
  options: MinimalPayloadOptions = {},
  root: JsonSchema = schema
): any => {
  const preferred = options.preferredEnumValues ?? DEFAULT_PREFERRED_ENUM_VALUES;
  const now = options.now ?? (() => new Date());
  const node = resolveRef(schema, root);

  if (Array.isArray(node.enum)) return pickEnum(node.enum, preferred);
  if (node.const !== undefined) return node.const;

  const type = Array.isArray(node.type) ? node.type[0] : node.type;
  switch (type) {
    case "object": {
      const result: Record<string, any> = {};
      for (const key of node.required ?? []) {
        result[key] = buildMinimalPayload(node.properties?.[key] ?? {}, options, root);
      }
      return result;
    }
    case "array": {
      const count = node.minItems ?? 0;
      return Array.from({ length: count }, () => buildMinimalPayload(node.items ?? {}, options, root));
    }
    case "string":
      if (node.format === "date-time") return now().toISOString();
      if (node.format === "uri") return "https://example.invalid";
      return "x".repeat(node.minLength ?? 0);
    case "integer":
    case "number": {
      if (typeof node.minimum === "number") return node.minimum;
      if (typeof node.exclusiveMinimum === "number") {
        return type === "integer" ? node.exclusiveMinimum + 1 : node.exclusiveMinimum + 0.1;
      }
      return 0;
    }
    case "boolean":
      return false;
    default:
      return {};
  }
};
